/**
 * Route-plan state for the assisted "Plan a passage" route step. Holds the few
 * choices the wizard asks (start point, manual vs automatic) and remembers which
 * route it plotted, so the step can switch from "ask the questions" to "you've
 * plotted it, now shape it" once a route exists.
 *
 * In MANUAL mode the wizard plots a straight line start -> destination, then the
 * user shapes it with the existing gestures (long-press the line to insert a
 * waypoint, drag waypoints to move). AUTOMATIC routing is a later stage.
 */
import { create } from 'zustand';
import type { Route, Waypoint } from '@/routing';
import type { RouteHazard } from '@/safety';
import type { PilotageSection } from '@/passage/pilotageSections';

export type StartMode = 'current' | 'point';
export type PlotMode = 'manual' | 'auto';

/**
 * An intermediate point the route must thread through. `dwellH` is how long we
 * stop there: 0 is a pure "go via" (a must-pass point, no stop), anything above 0
 * is a timed stopover that adds a dwell and shifts every downstream ETA. The
 * order in the list is the order the route visits them.
 */
export interface ViaStop {
  position: [number, number];
  /** Hours stopped here (0 = pass straight through). */
  dwellH: number;
}

interface RoutePlanState {
  /** Start from the boat's current position, or a chosen point. */
  startMode: StartMode;
  /** The chosen start point ([lon, lat]) when startMode is 'point'. */
  startPoint: [number, number] | null;
  /** Plan the timing by a departure (the scrubber time) or by a target arrival. */
  planBasis: 'depart' | 'arrive';
  /** Target arrival instant (UTC ms) when planBasis is 'arrive', else null. */
  targetArriveMs: number | null;
  /** Must-pass via points and timed stopovers, in visit order. */
  vias: ViaStop[];
  /** Manual rubber-band plotting, or automatic (later stage). */
  plotMode: PlotMode;
  /** The route this wizard plotted, so the step knows it's done the straight line. */
  plannedRouteId: string | null;
  /** Charted hazards near the plotted route's track, recomputed as it's shaped. */
  routeHazards: RouteHazard[];
  /** Route stretches that want closer pilotage (departure/arrival/shoaling/hazards). */
  pilotageSections: PilotageSection[];
  /** The departure time the advisor recommended/accepted (UTC ms), or null. */
  plannedDepartureMs: number | null;
  /** A pending auto-route request (picked up by the map, which has the chart cells). */
  autoRequest: { start: [number, number]; destination: [number, number] } | null;
  /** Auto-routing progress for the UI. */
  autoStatus: 'idle' | 'running' | 'failed';
  autoError: string | null;

  setStartMode: (m: StartMode) => void;
  setStartPoint: (p: [number, number] | null) => void;
  setPlanBasis: (b: 'depart' | 'arrive') => void;
  setTargetArriveMs: (ms: number | null) => void;
  /** Add a via point (dwellH 0) or a timed stop (dwellH > 0) at the end of the list. */
  addVia: (position: [number, number], dwellH?: number) => void;
  /** Set the dwell hours for the via at `index` (0 turns a stop back into a via). */
  setViaDwell: (index: number, dwellH: number) => void;
  removeVia: (index: number) => void;
  clearVias: () => void;
  setPlotMode: (m: PlotMode) => void;
  setPlannedRouteId: (id: string | null) => void;
  setRouteHazards: (h: RouteHazard[]) => void;
  setPilotageSections: (s: PilotageSection[]) => void;
  setPlannedDeparture: (ms: number | null) => void;
  requestAutoRoute: (start: [number, number], destination: [number, number]) => void;
  finishAuto: (error?: string) => void;
  reset: () => void;
}

export const useRoutePlanStore = create<RoutePlanState>((set) => ({
  startMode: 'current',
  startPoint: null,
  planBasis: 'depart',
  targetArriveMs: null,
  vias: [],
  plotMode: 'manual',
  plannedRouteId: null,
  routeHazards: [],
  pilotageSections: [],
  plannedDepartureMs: null,
  autoRequest: null,
  autoStatus: 'idle',
  autoError: null,

  setStartMode: (startMode) => set({ startMode }),
  setStartPoint: (startPoint) => set({ startPoint, startMode: 'point' }),
  setPlanBasis: (planBasis) => set({ planBasis }),
  setTargetArriveMs: (targetArriveMs) => set({ targetArriveMs }),
  addVia: (position, dwellH = 0) =>
    set((s) => ({ vias: [...s.vias, { position, dwellH: Math.max(0, dwellH) }] })),
  setViaDwell: (index, dwellH) =>
    set((s) => ({
      vias: s.vias.map((v, i) => (i === index ? { ...v, dwellH: Math.max(0, dwellH) } : v)),
    })),
  removeVia: (index) => set((s) => ({ vias: s.vias.filter((_, i) => i !== index) })),
  clearVias: () => set({ vias: [] }),
  setPlotMode: (plotMode) => set({ plotMode }),
  setPlannedRouteId: (plannedRouteId) => set({ plannedRouteId }),
  setRouteHazards: (routeHazards) => set({ routeHazards }),
  setPilotageSections: (pilotageSections) => set({ pilotageSections }),
  setPlannedDeparture: (plannedDepartureMs) => set({ plannedDepartureMs }),
  requestAutoRoute: (start, destination) => set({ autoRequest: { start, destination }, autoStatus: 'running', autoError: null }),
  finishAuto: (error) => set({ autoRequest: null, autoStatus: error ? 'failed' : 'idle', autoError: error ?? null }),
  reset: () => set({ startMode: 'current', startPoint: null, planBasis: 'depart', targetArriveMs: null, vias: [], plotMode: 'manual', plannedRouteId: null, routeHazards: [], pilotageSections: [], plannedDepartureMs: null, autoRequest: null, autoStatus: 'idle', autoError: null }),
}));

/**
 * Assemble a passage route: start, any via points / stops in order, then the
 * destination. A via with dwellH > 0 is a timed stop and gets a "Stop" name so it
 * reads clearly on the route; dwellH 0 is a plain "Via". The dwell itself feeds
 * the passage timing (simulatePassage), not the route geometry. Pure, so it's
 * easy to test. `makeWpId` supplies fresh waypoint ids.
 */
export function buildPassageRoute(args: {
  id: string;
  start: [number, number];
  destination: [number, number];
  vias?: { position: [number, number]; dwellH?: number }[];
  destName?: string;
  name?: string;
  makeWpId: () => string;
}): Route {
  const waypoints: Waypoint[] = [];
  waypoints.push({ id: args.makeWpId(), position: args.start, name: 'Start' });
  (args.vias ?? []).forEach((v, i) => {
    const isStop = (v.dwellH ?? 0) > 0;
    waypoints.push({
      id: args.makeWpId(),
      position: v.position,
      name: isStop ? `Stop ${i + 1}` : `Via ${i + 1}`,
    });
  });
  waypoints.push({ id: args.makeWpId(), position: args.destination, name: args.destName ?? 'Destination' });
  return { id: args.id, name: args.name ?? 'Passage route', waypoints };
}
